import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react'
import type maplibregl from 'maplibre-gl'
import {
	MapContainer,
	ClusterMarkers,
	DEFAULT_CENTER,
	DEFAULT_ZOOM,
} from 'features/Map'
import type { ClusterPoint } from 'features/Map'
import { tradeAreaService } from 'features/Map/services/tradeAreaService'

export type LocationCategory = 'center' | 'poi' | 'tenant' | 'active'

export interface LocationIndexMarker {
	id: string
	lat: number
	lng: number
	name?: string
	image?: string
	address?: string
	city?: string
	stateProvince?: string
	category: LocationCategory
}

export interface MapViewState {
	lat: number
	lng: number
	zoom: number
}

interface LocationsIndexMapProps {
	markers: LocationIndexMarker[]
	onMarkerClick: (markerId: string) => void
	initialView?: MapViewState | null
}

export interface LocationsIndexMapHandle {
	getViewState: () => MapViewState | null
}

const AVATAR_BORDER_COLOR = '#0080FE'
const CENTER_BORDER_COLOR = '#EF4444'
const POI_COLOR = '#F97316'
const TENANT_COLOR = '#22C55E'

const MARKER_SIZE = 32

function escapeAttr(v: string): string {
	return v
		.replace(/&/g, '&amp;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
}

function buildTeardropPin(color: string): HTMLDivElement {
	const el = document.createElement('div')
	el.style.cssText = `
		width:24px;height:32px;cursor:pointer;
		transition:transform 150ms ease;
	`
	el.innerHTML = `
		<svg viewBox="0 0 24 32" width="24" height="32" xmlns="http://www.w3.org/2000/svg">
			<path d="M12 0C5.37 0 0 5.37 0 12c0 9 12 20 12 20s12-11 12-20C24 5.37 18.63 0 12 0z"
				fill="${color}" stroke="#ffffff" stroke-width="2"/>
			<circle cx="12" cy="12" r="4" fill="#ffffff"/>
		</svg>
	`
	el.addEventListener('mouseenter', () => {
		el.style.transform = 'scale(1.15)'
	})
	el.addEventListener('mouseleave', () => {
		el.style.transform = 'scale(1)'
	})
	return el
}

function buildAvatarMarker(image: string, isCenter: boolean, name?: string): HTMLButtonElement {
	const btn = document.createElement('button')
	btn.type = 'button'
	const borderColor = isCenter ? CENTER_BORDER_COLOR : AVATAR_BORDER_COLOR
	const bg = isCenter ? '#ffffff' : 'transparent'
	btn.style.cssText = `
		position:relative;
		width:${MARKER_SIZE}px;height:${MARKER_SIZE}px;
		padding:0;margin:0;
		border-radius:50%;
		border:2px solid ${borderColor};
		background:${bg};
		box-shadow:0 0 6px 2px rgba(255,255,255,0.85);
		overflow:hidden;
		cursor:pointer;
		transition:transform 150ms ease;
	`
	if (name) btn.title = name
	btn.innerHTML = `
		<img src="${escapeAttr(image)}" alt="${escapeAttr(name || '')}" referrerpolicy="no-referrer"
			style="width:100%;height:100%;object-fit:cover;${isCenter ? 'filter:grayscale(1);' : ''}" />
		${isCenter ? `<span style="position:absolute;inset:0;border-radius:50%;background:rgba(239,68,68,0.3);pointer-events:none;"></span>` : ''}
	`
	btn.addEventListener('mouseenter', () => {
		btn.style.transform = 'scale(1.1)'
	})
	btn.addEventListener('mouseleave', () => {
		btn.style.transform = 'scale(1)'
	})
	return btn
}

function buildFallbackPin(color: string): HTMLDivElement {
	const el = document.createElement('div')
	el.style.cssText = `
		width:18px;height:18px;border-radius:50%;
		background:${color};
		border:2px solid #ffffff;
		box-shadow:0 2px 4px rgba(0,0,0,0.3);
		cursor:pointer;
		transition:transform 150ms ease;
	`
	el.addEventListener('mouseenter', () => {
		el.style.transform = 'scale(1.2)'
	})
	el.addEventListener('mouseleave', () => {
		el.style.transform = 'scale(1)'
	})
	return el
}

function renderLocationLeaf(point: ClusterPoint, onClick: () => void): HTMLElement {
	const props = point.properties as Partial<LocationIndexMarker> | undefined
	const category = (props?.category ?? 'active') as LocationCategory
	const image = props?.image
	const name = props?.name

	let el: HTMLElement
	if (category === 'poi') {
		el = buildTeardropPin(POI_COLOR)
	} else if (category === 'tenant') {
		el = buildTeardropPin(TENANT_COLOR)
	} else if (image) {
		el = buildAvatarMarker(image, category === 'center', name)
	} else {
		el = buildFallbackPin(category === 'center' ? CENTER_BORDER_COLOR : AVATAR_BORDER_COLOR)
	}
	el.addEventListener('click', (e) => {
		e.stopPropagation()
		onClick()
	})
	return el
}

const LocationsIndexMap = forwardRef<LocationsIndexMapHandle, LocationsIndexMapProps>(
	({ markers, onMarkerClick, initialView }, ref) => {
		const [map, setMap] = useState<maplibregl.Map | null>(null)
		const didInitViewRef = useRef(false)

		useImperativeHandle(
			ref,
			() => ({
				getViewState: () => {
					if (!map) return null
					const center = map.getCenter()
					return {
						lat: center.lat,
						lng: center.lng,
						zoom: map.getZoom(),
					}
				},
			}),
			[map],
		)

		const points = useMemo<ClusterPoint[]>(
			() =>
				markers
					.filter((m) => m.lat != null && m.lng != null)
					.map((m) => ({
						id: m.id,
						longitude: m.lng,
						latitude: m.lat,
						properties: {
							category: m.category,
							name: m.name,
							image: m.image,
						},
					})),
			[markers],
		)

		const handleMapLoad = useCallback((m: maplibregl.Map) => {
			setMap(m)
		}, [])

		useEffect(() => {
			if (!map || didInitViewRef.current) return
			if (initialView) {
				map.setCenter([initialView.lng, initialView.lat])
				map.setZoom(initialView.zoom)
			} else if (markers.length > 1) {
				const bounds = tradeAreaService.getBoundsForSites(
					markers.map((m) => ({ longitude: m.lng, latitude: m.lat })),
				)
				map.fitBounds(bounds, { padding: 60, maxZoom: 15, animate: false })
			} else if (markers.length === 1) {
				map.setCenter([markers[0].lng, markers[0].lat])
				map.setZoom(12)
			} else {
				map.setCenter(DEFAULT_CENTER)
				map.setZoom(DEFAULT_ZOOM)
			}
			didInitViewRef.current = true
		}, [map, initialView, markers])

		const renderLeaf = useCallback(renderLocationLeaf, [])

		return (
			<div className="shadow-xl relative rounded-lg overflow-hidden h-[680px]">
				<MapContainer onMapLoad={handleMapLoad}>
					<ClusterMarkers
						map={map}
						isMapLoaded={Boolean(map)}
						points={points}
						renderLeaf={renderLeaf}
						onLeafClick={onMarkerClick}
					/>
				</MapContainer>
				<div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-sm rounded-lg px-5 py-2 shadow-md flex items-center gap-6 text-xs z-10">
					<div className="flex items-center gap-1.5">
						<span className="w-3 h-3 rounded-full shrink-0 bg-[#0080FE]" />
						<span className="text-gray-600">Retail Location</span>
					</div>
					<div className="flex items-center gap-1.5">
						<span className="w-3 h-3 rounded-full bg-red-500 border border-red-600 shrink-0" />
						<span className="text-gray-600">Retail Center</span>
					</div>
				</div>
			</div>
		)
	},
)

LocationsIndexMap.displayName = 'LocationsIndexMap'

export default LocationsIndexMap
